import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '../../components/ui/Button';
import { ErrorNotice } from '../../components/ui/ErrorNotice';
import { Modal } from '../../components/ui/Modal';

/** Plain confirmation dialog used by Disable, Rotate key and Remove admin. */
export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  busyLabel,
  destructive = false,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  busyLabel?: string;
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={title}
      onClose={() => {
        setError(null);
        onCancel();
      }}
      width="sm"
      footer={
        <>
          <Button
            onClick={() => {
              setError(null);
              onCancel();
            }}
          >
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            disabled={busy}
            onClick={() => void confirm()}
          >
            {busy ? (busyLabel ?? 'Working…') : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-ink">{body}</p>
        {error ? <ErrorNotice error={error} /> : null}
      </div>
    </Modal>
  );
}
